import { IconClose } from "@/assets/icons/geist/IconClose";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import type { PropsWithTestId } from "@/components/podkit/lib/testid";
import { type PropsWithTrackLocation } from "@/hooks/use-segment";
import { type FC, type PropsWithChildren } from "react";

type NudgeProps = {
    open: boolean;
    toggleOpen: () => void;
    ariaLabelPrefix: string;
    showCloseButton?: boolean;
} & PropsWithTestId &
    PropsWithTrackLocation &
    PropsWithClassName &
    PropsWithChildren;

export const Nudge: FC<NudgeProps> = ({
    open,
    toggleOpen,
    ariaLabelPrefix,
    showCloseButton = true,
    className,
    children,
    "data-testid": testId,
    "data-track-location": trackLocation,
}) => {
    if (!open) {
        return null;
    }

    return (
        <div
            data-testid={testId}
            data-track-location={trackLocation}
            role="region"
            aria-live="polite"
            aria-label={ariaLabelPrefix}
            className={cn(
                "relative w-full max-w-[270px] flex-col rounded-2xl border-[0.5px] border-border-base/50 bg-surface-glass p-2 shadow-md backdrop-blur-modal",
                "animate-slideUpAndFadeIn",
                "hidden md:flex",
                className,
            )}
        >
            {showCloseButton && (
                <button
                    className="absolute right-3 top-3 z-10 inline-flex rounded-[4px] bg-transparent outline-none ring-0 hover:bg-surface-03 focus:ring-0 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-0 focus-visible:outline-content-primary active:outline-0 disabled:bg-transparent disabled:text-content-secondary"
                    onClick={toggleOpen}
                    aria-label={`${ariaLabelPrefix} - Dismiss`}
                    data-tracking-id={`dismiss-nudge`}
                >
                    <IconClose size="sm" />
                </button>
            )}
            {children}
        </div>
    );
};
